import React from 'react';
import {
  Stack,
  Typography,
  Button,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { AsyncBox } from '@fortress-technology-solutions/fortress-component-library/Molecules';
import { Collapse } from '@fortress-technology-solutions/fortress-component-library/Transitions';
import {
  VisibilityIcon,
  VisibilityOffIcon,
  PlusIcon,
} from '@fortress-technology-solutions/fortress-component-library/Icons';

import EditableRow from './EditableRow';
import useIntl from '../../../../hooks/useIntl';
import messages from './messages';

const SecurityDepositInterestTable = ({
  editMode,
  securityDepositInterestRates,
  setSecurityDepositInterestRates,
  editableRates,
  modifiedRates,
  setModifiedRates,
  newInterestRatesToCreate,
  setNewInterestRatesToCreate,
  accrueSecurityDepositInterest,
  isLoading,
}) => {
  const intl = useIntl();
  const [showHistory, setShowHistory] = React.useState(false);
  const mostRecentRate = securityDepositInterestRates.slice(-1)[0] || null;
  const mostRecentRateIsEditable =
    editableRates.find((rate) => rate.id === mostRecentRate?.id) !==
      undefined && accrueSecurityDepositInterest;

  function addNewInterestRate() {
    setNewInterestRatesToCreate((prev) => {
      return [
        ...prev,
        {
          startDate: null,
          endDate: null,
          interest: null,
          startDateIsEditable: true,
          endDateIsEditable: true,
        },
      ];
    });
  }

  return (
    <AsyncBox px={2} loading={isLoading}>
      <Stack direction={'row'} spacing={4} pb={1}>
        <Typography
          variant="labelLarge"
          sx={{
            flex: 1,
          }}
        >
          {intl.formatMessage(messages.startDate)}
        </Typography>
        <Typography variant="labelLarge" sx={{ flex: 1 }}>
          {intl.formatMessage(messages.endDate)}
        </Typography>
        <Typography variant="labelLarge" sx={{ flex: 1 }}>
          {intl.formatMessage(messages.interestRate)}
        </Typography>
      </Stack>
      <Collapse in={showHistory}>
        <Stack direction={'column'} spacing={2}>
          {securityDepositInterestRates
            .filter((rate) => {
              return editMode && accrueSecurityDepositInterest
                ? editableRates.find(
                    (editableRate) => editableRate.id === rate.id,
                  ) === undefined
                : true;
            })
            .map((rate) => {
              return (
                <Stack key={rate.id} direction={'row'} spacing={4}>
                  <Typography variant="bodyMedium" sx={{ flex: 1 }}>
                    {rate.plainStartDate}
                  </Typography>
                  <Typography variant="bodyMedium" sx={{ flex: 1 }}>
                    {rate.plainEndDate || '---'}
                  </Typography>
                  <Typography variant="bodyMedium" sx={{ flex: 1 }}>
                    {rate.interest}%
                  </Typography>
                </Stack>
              );
            })}
        </Stack>
      </Collapse>
      <Collapse in={!showHistory && (!mostRecentRateIsEditable || !editMode)}>
        <Stack direction={'column'} spacing={2}>
          <Stack direction={'row'} spacing={4}>
            <Typography variant="bodyMedium" sx={{ flex: 1 }}>
              {mostRecentRate?.plainStartDate || '---'}
            </Typography>
            <Typography variant="bodyMedium" sx={{ flex: 1 }}>
              {mostRecentRate?.plainEndDate || '---'}
            </Typography>
            <Typography variant="bodyMedium" sx={{ flex: 1 }}>
              {mostRecentRate?.interest != null
                ? `${mostRecentRate?.interest}%`
                : '---'}
            </Typography>
          </Stack>
        </Stack>
      </Collapse>
      {editMode && accrueSecurityDepositInterest && (
        <>
          {editableRates.map((rate) => (
            <EditableRow
              key={rate.id}
              idx={null}
              id={rate.id}
              interestRate={rate}
              setNewInterestRatesToCreate={setNewInterestRatesToCreate}
              newInterestRatesToCreate={newInterestRatesToCreate}
              alreadyExistingRates={securityDepositInterestRates}
              setAlreadyExistingRates={setSecurityDepositInterestRates}
              modifiedRates={modifiedRates}
              setModifiedRates={setModifiedRates}
            />
          ))}

          {newInterestRatesToCreate.map((rate, idx) => (
            <EditableRow
              key={idx}
              idx={idx}
              id={null}
              interestRate={rate}
              setNewInterestRatesToCreate={setNewInterestRatesToCreate}
              newInterestRatesToCreate={newInterestRatesToCreate}
              alreadyExistingRates={securityDepositInterestRates}
              setAlreadyExistingRates={setSecurityDepositInterestRates}
              modifiedRates={modifiedRates}
              setModifiedRates={setModifiedRates}
            />
          ))}
        </>
      )}
      <Stack>
        {editMode && (
          <Stack
            direction="row"
            justifyContent="flex-start"
            alignItems="center"
          >
            <Button
              variant="text"
              onClick={addNewInterestRate}
              size="small"
              startIcon={<PlusIcon />}
              disabled={!accrueSecurityDepositInterest}
            >
              {intl.formatMessage(messages.addNewRate)}
            </Button>
          </Stack>
        )}
        <Stack direction="row" justifyContent="flex-start" alignItems="center">
          <Button
            variant="text"
            onClick={() => setShowHistory(!showHistory)}
            startIcon={showHistory ? <VisibilityOffIcon /> : <VisibilityIcon />}
            disabled={!securityDepositInterestRates.length > 0}
          >
            {intl.formatMessage(
              showHistory ? messages.hideHistory : messages.showHistory,
            )}
          </Button>
        </Stack>
      </Stack>
    </AsyncBox>
  );
};

export default SecurityDepositInterestTable;
